import React, { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const SECTIONS = [
  {
    title: "Rental & Timing Policy",
    body: [
      "Kilometer limit will be as per the rate card. Extra Km charges: Scooters Rs 5/Km, Geared Bikes Rs 10/Km.",
      "Delay charges: Scooters Rs 50/hour, Motor Bikes Rs 100/hour (Maximum 6 hours).",
      "A rental day is considered 24 hours.",
      "15 minutes grace period only during drop-off. After that, full day rental applies.",
      "1 Month Rental = 30 Days.",
      "Bike must be returned during office timing only.",
      "Pickup & Drop-off service is not available.",
    ],
  },
  {
    title: "Fuel & Cleanliness",
    body: [
      "Fuel level provided during checkout must be returned in the same quantity.",
      "Excess fuel will not be refunded.",
      "Excessively dirty or muddy vehicles will attract washing charges up to Rs 200.",
    ],
  },
  {
    title: "Damage & Accident Policy",
    body: [
      "YOU DAMAGE YOU PAY policy is applicable.",
      "Any new damage caused during rental must be fully paid by the hirer.",
      "In major accidents, 50% rental amount must be paid until the vehicle is repaired.",
      "Damage Coverage for scooters available at Rs 60/day.",
      "RR Bros will not get involved in third-party accident settlements.",
      "Damage due to accident/mishandling/carelessness will be charged as per vendor estimate.",
      "Customer is liable to pay daily tariff until the bike is ready for rental again.",
    ],
  },
  {
    title: "Fines & Penalties",
    body: [
      "Metal Key Tag lost – Rs 100 fine.",
      "Helmet lost/damaged – Rs 1500 fine.",
      "Mobile Holder lost/damaged – Rs 250 fine.",
      "Key lost – Rs 1000 fine.",
      "Triple riding – Rs 1000 fine.",
      "Unauthorized modifications/stickers/repairs – Rs 2000 fine.",
    ],
  },
  {
    title: "Helmet & Safety Rules",
    body: [
      "One helmet is provided free with rental.",
      "Extra helmet – Rs 50/day per helmet.",
      "Travelling from Padubidri to Mangalore requires compulsory 2 helmets.",
    ],
  },
  {
    title: "Customer Responsibility",
    body: [
      "Vehicle cannot be lent to another person without informing RR Bros.",
      "Additional rider documents must be verified before the trip.",
      "RR Bros reserves the right to seize the vehicle if problems are caused by the hirer.",
      "The rider uses the bike entirely at his/her own risk.",
      "RR Bros is not responsible for any injury, loss or death.",
      "Bike must be returned in the same condition as handed over.",
    ],
  },
  {
    title: "Mechanical & Towing Policy",
    body: [
      "Customer must inform RR Bros before doing any repairs.",
      "Original printed invoice is compulsory for reimbursement.",
      "Towing charges due to puncture/collision/improper usage will be borne by customer.",
      "Mechanical or technical failure, towing will be handled by RR Bros.",
    ],
  },
  {
    title: "Prohibited Uses",
    body: [
      "Vehicles cannot be used for rallies, races, competitions or media usage.",
      "Driving under influence of alcohol/drugs is strictly prohibited.",
      "Illegal activities using the vehicle are strictly prohibited.",
    ],
  },
  {
    title: "Booking & Refund Policy",
    body: [
      "Rental Pause applicable only for rentals above 3 months.",
      "Booking amount once paid is NON-REFUNDABLE.",
      "No cancellation, refund or date adjustment after booking.",
    ],
  },
];

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-neutral-900 antialiased">
      <SEO
        title="Terms & Conditions | RR Bros Bike Rental Udupi & Manipal"
        description="Read the rental terms & conditions for RR Bros Bike Rental Udupi and Manipal — timing, fuel, damage, helmet, refund and safety policies for scooty rental."
        canonical="https://ezrental.in/terms"
        keywords="RR Bros terms and conditions, bike rental terms Udupi, scooty rental policy Manipal, self drive bike rental rules"
      />
      <Header />

      {/* Page banner */}
      <section className="bg-neutral-900 text-white py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-orange-400 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>
          <div className="block">
            <span className="text-orange-400 font-bold text-sm uppercase tracking-[0.2em]">
              RR Bros Bike Rental
            </span>
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-black leading-tight tracking-tight">
            Terms &amp; <span className="text-orange-500">Conditions</span>
          </h1>
          <p className="mt-5 text-neutral-300 max-w-2xl text-sm sm:text-base leading-relaxed">
            <span className="block font-semibold text-white">Office Timing</span>
            07:30 AM – 07:30 PM (Udupi) &nbsp;|&nbsp; 09:00 AM – 07:30 PM (Manipal)
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 sm:space-y-8">
          {SECTIONS.map((s, idx) => (
            <div
              key={idx}
              className="bg-neutral-50 border border-neutral-100 rounded-2xl p-6 sm:p-8"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-black text-sm flex-shrink-0">
                  {idx + 1}
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-neutral-900 uppercase tracking-wide">
                  {s.title}
                </h2>
              </div>
              <ul className="space-y-2.5 text-neutral-700 text-sm sm:text-base leading-relaxed pl-11">
                {s.body.map((p, i) => (
                  <li key={i} className="relative before:content-['•'] before:absolute before:-left-4 before:text-orange-500 before:font-black">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Agreement statement */}
          <div className="bg-orange-500 text-white rounded-2xl p-6 sm:p-8 text-center">
            <p className="text-sm sm:text-base font-bold uppercase tracking-wider leading-relaxed">
              After reading the above Terms &amp; Conditions, the renter agrees
              to all conditions and proceeds with payment for booking.
            </p>
          </div>

          <p className="text-xs text-neutral-500 text-center pt-2">
            Last updated:{" "}
            {new Date().toLocaleDateString("en-IN", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsConditions;
